import Head from "next/head";
import { useRouter } from "next/router";

import {
  RhButton,
  RhCard,
  RhDivider,
  RhIcon,
  RhInput,
  RhInputGroup,
  RhSkeletonRectangle,
  RhTabs,
  RhTabsItem,
} from "@rhythm-ui/react";
import { useInfiniteQuery } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";

import { JobFilterParamType, JobType } from "@/types/job";

import api from "@/services/api";
import publicApi from "@/services/publicApi";

import { LAKH_CURRENCY_MULTIPLIER } from "@/constants";
import mixPanelEventName from "@/constants/mixPanelEventName";

import useTrackEvent from "@/hooks/useTrackEvent";

import NavBar from "@/components/layout/NavBar";

// import CandidateCardSkeleton from "@/components/skeletons/candidate/CandidateCardSkeleton";

import JobApplicationStatusDrawer from "./components/JobApplicationStatusDrawer";
import JobCard from "./components/JobCard";
import JobCardMobileView from "./components/JobCardMobileView";
import JobDetailsDrawer from "./components/JobDetailsDrawer";
import JobFilter from "./components/JobFilter";

const defaultFilters = {
  ordering: "-modified_ts",
  status: "Active",
};

// const defaultFiltersKeys = Object.keys(defaultFilters).sort();

const isPageFirstLoad = true;

const CandidateJobs = ({
  serverFetchedJobDetails,
  serverFetchedJobList,
  isPagePublic,
}: {
  serverFetchedJobDetails?: JobType;
  serverFetchedJobList?: { count: number; results: JobType[]; next: string };
  isPagePublic?: boolean;
}) => {
  const [filterJobsOn, setFilterJobsOn] = useState(
    defaultFilters as JobFilterParamType,
  );

  const [openJobApplicationStatusDrawer, setOpenJobApplicationStatusDrawer] =
    useState<null | JobType>(null);

  const formattedFilter = formatFilterForApi(filterJobsOn);

  const {
    data: privateJobListData,
    fetchNextPage: fetchPrivateNextJobsPage,
    // hasNextPage: privateListHasNextPage,
    isLoading: isPrivateListLoading,
  } = useInfiniteQuery({
    queryKey: ["jobs", formattedFilter],
    queryFn: async ({ pageParam = 1 }) => {
      return api.company.getJobs({ ...formattedFilter, page: pageParam });
    },
    select: (data) => {
      return { ...data, pages: data.pages.map((page) => page.data) };
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.data?.next) return;
      const page = new URLSearchParams(new URL(lastPage.data.next).search).get(
        "page",
      );
      return page ?? false;
    },
    enabled: !isPagePublic && isFilterValid(filterJobsOn),
  });

  const {
    data: publicJobListData,
    fetchNextPage: fetchPublicNextJobsPage,
    // hasNextPage: publicListHasNextPage,
    isLoading: isPublicListLoading,
  } = useInfiniteQuery({
    queryKey: ["jobs", formattedFilter],
    queryFn: async ({ pageParam = 1 }) => {
      return publicApi.getJobs({ ...formattedFilter, page: pageParam });
    },
    select: (data) => {
      return { ...data, pages: data.pages.map((page) => page.data) };
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.data?.next) return;
      const page = new URLSearchParams(new URL(lastPage.data.next).search).get(
        "page",
      );
      return page ?? false;
    },
    enabled: isPagePublic && isFilterValid(filterJobsOn),
  });

  const jobListData = !isPagePublic
    ? (privateJobListData ?? {
        pages: serverFetchedJobList ? [serverFetchedJobList] : [],
      })
    : (publicJobListData ?? {
        pages: serverFetchedJobList ? [serverFetchedJobList] : [],
      });

  const fetchNextJobsPage = !isPagePublic
    ? fetchPrivateNextJobsPage
    : fetchPublicNextJobsPage;

  // const hasNextPage = !isPagePublic
  //   ? privateListHasNextPage
  //   : publicListHasNextPage;

  const isJobListLoading = !isPagePublic
    ? isPrivateListLoading
    : isPublicListLoading;

  const [openJobDrawerId, setOpenJobDrawerId] = useState<number | null>(null);

  const [showJobResultNavBar, setShowJobResultNavBar] =
    useState(!isPageFirstLoad);

  // // Don't load job list, when job detail page(job detail drawer) is being scrapped by crawler
  // // might find this conflicting. Only when drawer is closed then, load job list details (๏ᆺ๏υ)
  const onFirstPageRenderIsJobDetailDrawerViewOpen = !!serverFetchedJobDetails;
  const [shouldJobListRender, setShouldJobListRender] = useState(
    !onFirstPageRenderIsJobDetailDrawerViewOpen,
  );
  // after the drawer is closed, render the job list
  useEffect(() => {
    if (!shouldJobListRender && openJobDrawerId === null)
      setShouldJobListRender(true);
  }, [openJobDrawerId]);

  const router = useRouter();

  const [wasDrawerExplicitlyClosed, setWasDrawerExplicitlyClosed] =
    useState(false);

  useEffect(() => {
    if (wasDrawerExplicitlyClosed) {
      // If drawer was explicitly closed, don't reopen it
      return;
    }

    if (router.isReady && router.query.id) {
      const jobId = parseInt(router.query.id as string, 10);
      if (!isNaN(jobId)) {
        setOpenJobDrawerId(jobId);
      } else {
        setOpenJobDrawerId(serverFetchedJobDetails?.id || null);
      }
    } else {
      setOpenJobDrawerId(serverFetchedJobDetails?.id || null);
    }
  }, [
    router.isReady,
    router.query.id,
    serverFetchedJobDetails?.id,
    wasDrawerExplicitlyClosed,
  ]);

  const onAllFilterClear = () => {
    setFilterJobsOn({ ...defaultFilters, is_applied: filterJobsOn.is_applied });
  };

  const onJobCardClick = (jobId: number) => {
    router.push(`/jobs?id=${jobId}`, undefined, { shallow: true });
    setOpenJobDrawerId(jobId);
  };

  const onTabClickHandler = (value: boolean) => {
    if (value === true) {
      setFilterJobsOn((state) => ({
        ...state,
        is_applied: true,
        status: "Active&status=Hold",
      }));
    } else {
      setFilterJobsOn((state) => {
        const newState = { ...state };
        delete newState.is_applied;
        newState.status = "Active";
        return newState;
      });
    }
  };

  const totalPageCount = jobListData?.pages[0]?.count ?? 0;

  const jobList = jobListData.pages.reduce(
    (acc, page) => [...acc, ...page.results],
    [] as JobType[],
  );

  const { ref, inView } = useInView();

  React.useEffect(() => {
    if (inView) {
      fetchNextJobsPage();
    }
  }, [inView]);

  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  return (
    <>
      <Head>
        {!openJobDrawerId ? (
          <title>Jobs | Zelevate</title>
        ) : (
          <title>Job Details | Zelevate</title>
        )}
      </Head>
      <div className="flex h-screen flex-col">
        <NavBar />
        {!isPagePublic && !isMobileFilterOpen && (
          <div className="flex items-center justify-between px-[0px] 2xl:container lg:px-[40px] 2xl:mx-auto">
            <RhTabs
              className="[&>*]:select-none"
              defaultActiveSection={!!(filterJobsOn.is_applied === true)}
              //  TODO: this is a limiation from the tab component. We need a controlled a component
              // here, so that if we were to change the value of tab, it reflect the same in the ui
              // since, it is not happening, we are remoundting the component, so that it can use the `defaultActiveSection` prop
              // not happy about this hack. Will have to create a custom tab, so that we can overcome this limiation (TODO)
              key={
                filterJobsOn.is_applied
                  ? JSON.stringify(filterJobsOn.is_applied)
                  : ""
              }
              onTabClick={onTabClickHandler}
            >
              <RhTabsItem title={`Browse`} value={false} />
              <RhTabsItem title={`Applied`} value={true} />
            </RhTabs>

            <RhIcon
              icon="tabler:list-search"
              className="text-hint mr-tmini h-[20px] w-[20px] lg:hidden"
              onClick={() => setIsMobileFilterOpen((state) => !state)}
            />
          </div>
        )}
        <div className="background-blue flex flex-1 overflow-auto">
          <div className="relative hidden flex-1 gap-tm2 px-[40px] pt-tm2 2xl:container lg:flex 2xl:mx-auto">
            <div className="flex h-full w-[272px] flex-col">
              <JobFilter
                filterJobsOn={filterJobsOn}
                setFilterJobsOn={setFilterJobsOn}
                onAllFilterClear={onAllFilterClear}
                isPagePublic={isPagePublic}
              />
            </div>
            <div className="flex h-full flex-1 flex-col">
              <JobListTextSearchInput
                filterJobsOn={filterJobsOn}
                setFilterJobsOn={setFilterJobsOn}
              />
              <div className="flex-1 overflow-auto">
                <div className="relative mr-tmini">
                  {shouldJobListRender && !isJobListLoading && (
                    <>
                      {jobListData?.pages.map((page, pageIndex) => (
                        <React.Fragment key={pageIndex}>
                          {page.results.map((job) => {
                            // Log job object to console

                            return (
                              <JobCard
                                key={job.id}
                                job={job}
                                onJobCardClick={onJobCardClick}
                                setOpenJobApplicationStatusDrawer={
                                  setOpenJobApplicationStatusDrawer
                                }
                                className="relative z-10"
                              />
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </>
                  )}
                  <div
                    className="absolute bottom-0 top-[60%] w-full"
                    ref={ref}
                  />
                </div>

                {jobListData?.pages[0]?.results?.length === 0 && (
                  <div className="flex h-full w-full flex-col items-center justify-center gap-tsm">
                    <h5>No Matching Jobs found</h5>
                    <p className="text-hint mx-tlg max-w-[600px] text-center">
                      Try changing the filters or search criteria to find more
                      jobs
                    </p>
                  </div>
                )}

                {isJobListLoading && (
                  <div className="space-y-tsm">
                    {new Array(4).fill("").map((_, idx) => (
                      <RhCard className="" key={idx}>
                        <div className="space-y-tsm px-tsm py-tm1">
                          <RhSkeletonRectangle className="!h-[25px] !w-3/4" />
                          <RhSkeletonRectangle className="!h-[18px]2 !w-2/4" />
                          <RhSkeletonRectangle className="!h-[18px]3 !w-1/4" />
                          <div className="border-b border-dashed"></div>
                          <div className="flex justify-between gap-tmini">
                            <RhSkeletonRectangle className="!h-[18px]" />
                            <RhSkeletonRectangle className="!h-[18px]" />
                            <RhSkeletonRectangle className="!h-[18px]" />
                            <RhSkeletonRectangle className="!h-[18px]" />
                          </div>
                        </div>

                        <RhDivider />
                        <div className="space-y-tmini px-tsm py-tpico">
                          <RhSkeletonRectangle className="ml-auto !h-[42px] !rounded" />
                        </div>
                      </RhCard>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="block w-full lg:hidden">
            {isMobileFilterOpen ? (
              <>
                <JobFilter
                  filterJobsOn={filterJobsOn}
                  setFilterJobsOn={setFilterJobsOn}
                  onAllFilterClear={onAllFilterClear}
                  isPagePublic={isPagePublic}
                />
                <div className="mx-tsm pb-tmini">
                  <RhButton block onClick={() => setIsMobileFilterOpen(false)}>
                    Done
                  </RhButton>
                </div>
              </>
            ) : (
              <>
                <JobListTextSearchInput
                  filterJobsOn={filterJobsOn}
                  setFilterJobsOn={setFilterJobsOn}
                />
                <div className="overflow-auto">
                  <div className="relative mx-tnano">
                    {!isJobListLoading && (
                      <>
                        {jobListData?.pages.map((page, pageIndex) => (
                          <React.Fragment key={pageIndex}>
                            {page.results.map((job) => (
                              <JobCardMobileView
                                key={job.id}
                                job={job}
                                onJobCardClick={onJobCardClick}
                                className="relative z-10"
                              />
                            ))}
                          </React.Fragment>
                        ))}
                      </>
                    )}
                    <div
                      className="absolute bottom-0 top-[60%] w-full"
                      ref={ref}
                    />
                  </div>

                  {jobListData?.pages[0]?.results?.length === 0 && (
                    <div className="mt-tm2 flex h-full w-full flex-col items-center justify-center gap-tsm">
                      <h5>No Matching Jobs found</h5>
                      <p className="text-hint mx-tlg max-w-[600px] text-center">
                        Try changing the filters or search criteria to find more
                        jobs
                      </p>
                    </div>
                  )}

                  {isJobListLoading && (
                    <div className="space-y-tsm">
                      {new Array(4).fill("").map((_, idx) => (
                        <RhCard className="" key={idx}>
                          <div className="space-y-tsm px-tsm py-tm1">
                            <RhSkeletonRectangle className="!h-[25px] !w-3/4" />
                            <RhSkeletonRectangle className="!h-[18px]2 !w-2/4" />
                            <RhSkeletonRectangle className="!h-[18px]3 !w-1/4" />
                            <div className="border-b border-dashed"></div>
                            <div className="flex justify-between gap-tmini">
                              <RhSkeletonRectangle className="!h-[18px]" />
                              <RhSkeletonRectangle className="!h-[18px]" />
                              <RhSkeletonRectangle className="!h-[18px]" />
                              <RhSkeletonRectangle className="!h-[18px]" />
                            </div>
                          </div>

                          <RhDivider />
                          <div className="space-y-tmini px-tsm py-tpico">
                            <RhSkeletonRectangle className="ml-auto !h-[42px] !rounded" />
                          </div>
                        </RhCard>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <JobDetailsDrawer
        openJobDrawerId={openJobDrawerId}
        setOpenJobDrawerId={setOpenJobDrawerId}
        jobResultCount={totalPageCount}
        jobList={jobList ?? []}
        loadMoreJobs={() => fetchNextJobsPage()}
        showJobResultNavBar={showJobResultNavBar}
        setShowJobResultNavBar={setShowJobResultNavBar}
        isPagePublic={isPagePublic}
        serverFetchedJobDetails={serverFetchedJobDetails}
        setWasDrawerExplicitlyClosed={setWasDrawerExplicitlyClosed}
      />
      <JobApplicationStatusDrawer
        openJobApplicationStatusDrawer={openJobApplicationStatusDrawer}
        setOpenJobApplicationStatusDrawer={setOpenJobApplicationStatusDrawer}
      />
    </>
  );
};

export default CandidateJobs;

const isFilterValid = (filterJobOn: JobFilterParamType) => {
  if (filterJobOn.min_exp && filterJobOn.max_exp) {
    if (filterJobOn.min_exp >= filterJobOn.max_exp) return false;
  }

  if (filterJobOn.max_salary && filterJobOn.min_salary) {
    if (filterJobOn.min_salary >= filterJobOn.max_salary) return false;
  }
  return true;
};

// format filter params for api request
const formatFilterForApi = (filterParams: JobFilterParamType) => {
  const params = { ...filterParams };

  if (params.is_applied === false) {
    delete params.is_applied;
  }

  if (params.search) {
    params.search = params.search.toLowerCase();
    if (params.search === "") delete params.search;
  }

  if (params.status === "All") {
    delete params.status;
  }

  if (params.job_role_details__selected) {
    params.job_role__id = params.job_role_details__selected.id?.toString();
    delete params.job_role_details__selected;
  }
  if (params.skills__selected) {
    if (params.skills__selected.length > 0) {
      params.skills = params.skills__selected
        .map((skill) => skill.id)
        .join(",");
    }
    delete params.skills__selected;
  }

  if (params.work_preference_new__selected) {
    params.work_preference_new = params.work_preference_new__selected.join(",");
    delete params.work_preference_new__selected;
  }

  if (params.city_location_new__selected) {
    if (params.city_location_new__selected.length > 0) {
      params.location_new__in = params.city_location_new__selected
        .map((city) => city.id)
        .join(",");
    }
    delete params.city_location_new__selected;
  }

  if (params.min_salary) {
    params.min_salary = params.min_salary * LAKH_CURRENCY_MULTIPLIER;
  }

  if (params.max_salary) {
    params.max_salary = params.max_salary * LAKH_CURRENCY_MULTIPLIER;
  }

  if (params.company__selected) {
    params.company__in = params.company__selected
      .map((company) => company.id)
      .join(",");
    delete params.company__selected;
  }

  if (params.owner__selected) {
    if (params.owner__selected.length > 0)
      params.owner = params.owner__selected.join(",");
    delete params.owner__selected;
  }
  return params;
};

const JobListTextSearchInput = ({
  // jobResultCount,
  // filterJobsOn,
  setFilterJobsOn,
}: {
  filterJobsOn: JobFilterParamType;
  // jobResultCount: number;
  setFilterJobsOn: React.Dispatch<React.SetStateAction<JobFilterParamType>>;
}) => {
  //
  // const filterJobsOnKeys = Object.keys(filterJobsOn).sort();
  //
  const [searchText, setSearchText] = useState("");

  const searchChangeDebounceTimerId = useRef<ReturnType<
    typeof setInterval
  > | null>(null);

  const triggerEvent = useTrackEvent();

  const onSearchChangeHandler = (value: string) => {
    setSearchText(value);

    if (searchChangeDebounceTimerId.current)
      clearTimeout(searchChangeDebounceTimerId.current);

    searchChangeDebounceTimerId.current = setTimeout(() => {
      setFilterJobsOn((state) => ({ ...state, search: value }));
      triggerEvent({
        name: mixPanelEventName.CHANGE_FILTER,
        properties: { filterUsed: "Search" },
      });
    }, 700);
  };
  //
  return (
    <div className="mb-tmini flex items-center lg:ml-auto lg:w-[320px] [&>.field]:w-full">
      <RhInputGroup className="flex gap-2 rounded-none border-x-0 border-secondary-200 bg-white px-tmini lg:mr-tmini lg:rounded lg:border-x-[1px] lg:border-inherit">
        <RhInput
          type={"text"}
          onChange={(e) => onSearchChangeHandler(e.target.value)}
          placeholder="Search Title, Role or Company"
          value={searchText}
          className="w-full bg-white text-sm lg:w-[320px] lg:text-inherit"
        />
        <RhIcon className="text-hint icon-md" icon="tabler:search" />
      </RhInputGroup>
    </div>
  );
};
// </div>
